// ============================================================================
// FNE-BRIDGE - Store Factures (Zustand)
// ============================================================================

import { create } from 'zustand';
import type {
  FNEInvoice,
  FNEInvoiceItem,
  ImportedInvoice,
} from '../types/models';
import type {
  InvoiceType,
  InvoiceTemplate,
  PaymentMethod,
  FneStatus,
  VatType,
} from '../types/enums';
import type { InvoiceFilters, ImportProgressEvent } from '../types/api';
import { UNITE_DEFAULT } from '../constants/units';

// ============================================================================
// Types du state
// ============================================================================

/** Données du formulaire multi-étapes (7 étapes) */
export interface InvoiceFormData {
  // Étape 1 — Établissement & Point de vente
  etablissement: string;
  point_de_vente: string;
  message_commercial: string;
  pied_de_page: string;
  // Étape 2 — Type de facture
  type_facture: InvoiceType | '';
  mode_paiement: PaymentMethod | '';
  template: InvoiceTemplate | '';
  est_rne: boolean;
  numero_rne: string;
  // Étape 3 — Client
  client_ncc: string;
  client_nom: string;
  client_telephone: string;
  client_email: string;
  client_vendeur: string;
  devise_etrangere: string;
  taux_devise: number;
  // Étape 4 — Articles
  items: InvoiceItemFormEntry[];
  // Étape 5 — Remises
  remise_pourcentage: number;
  remise_montant: number;
  // Étape 6 — Taxes
  taxes_additionnelles: { nom: string; montant: number }[];
  // Extras
  facture_importee_id: string;
  facture_origine_id: string;
  notes: string;
}

export interface InvoiceItemFormEntry {
  _temp_id: string; // ID temporaire côté frontend
  quantite: number;
  reference: string;
  designation: string;
  prix_unitaire_ht: number;
  remise_pourcentage: number;
  taux_tva: number;
  type_tva: VatType;
  unite_mesure: string;
  taxes_personnalisees: { nom: string; montant: number }[];
  article_id: string;
}

interface InvoiceState {
  // --- Liste factures FNE ---
  fneInvoices: FNEInvoice[];
  fneInvoicesTotal: number;
  fneInvoicesLoading: boolean;
  fneInvoicesFilters: InvoiceFilters;

  // --- Liste factures importées ---
  importedInvoices: ImportedInvoice[];
  importedInvoicesTotal: number;
  importedInvoicesLoading: boolean;

  // --- Facture courante (visualisation) ---
  currentInvoice: FNEInvoice | null;
  currentInvoiceItems: FNEInvoiceItem[];
  currentInvoiceLoading: boolean;

  // --- Formulaire multi-étapes ---
  formStep: number; // 1-7
  formData: InvoiceFormData;
  formErrors: Record<string, string>;
  formSubmitting: boolean;
  editingInvoiceId: string | null; // ID de la facture en cours d'édition (null = création)

  // --- Import ---
  importProgress: ImportProgressEvent | null;
  importRunning: boolean;

  // --- Erreur générale ---
  error: string | null;

  // --- Actions liste ---
  setFneInvoices: (invoices: FNEInvoice[], total: number) => void;
  setFneInvoicesLoading: (loading: boolean) => void;
  setFneInvoicesFilters: (filters: Partial<InvoiceFilters>) => void;
  setImportedInvoices: (invoices: ImportedInvoice[], total: number) => void;
  setImportedInvoicesLoading: (loading: boolean) => void;

  // --- Actions facture courante ---
  setCurrentInvoice: (invoice: FNEInvoice | null, items?: FNEInvoiceItem[]) => void;
  setCurrentInvoiceLoading: (loading: boolean) => void;

  // --- Actions formulaire ---
  setFormStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateFormData: (updates: Partial<InvoiceFormData>) => void;
  addItem: (item: InvoiceItemFormEntry) => void;
  updateItem: (tempId: string, updates: Partial<InvoiceItemFormEntry>) => void;
  removeItem: (tempId: string) => void;
  addTaxeAdditionnelle: (tax: { nom: string; montant: number }) => void;
  removeTaxeAdditionnelle: (index: number) => void;
  setFormErrors: (errors: Record<string, string>) => void;
  clearFormErrors: () => void;
  setFormSubmitting: (submitting: boolean) => void;
  prefillFromImport: (imported: ImportedInvoice) => void;
  prefillFromDraft: (invoice: FNEInvoice, items: FNEInvoiceItem[]) => void;
  resetForm: () => void;

  // --- Actions import ---
  setImportProgress: (progress: ImportProgressEvent | null) => void;
  setImportRunning: (running: boolean) => void;

  // --- Erreur ---
  setError: (error: string | null) => void;
}

// ============================================================================
// State initial formulaire
// ============================================================================

const initialFormData: InvoiceFormData = {
  etablissement: '',
  point_de_vente: '',
  message_commercial: '',
  pied_de_page: '',
  type_facture: '',
  mode_paiement: '',
  template: '',
  est_rne: false,
  numero_rne: '',
  client_ncc: '',
  client_nom: '',
  client_telephone: '',
  client_email: '',
  client_vendeur: '',
  devise_etrangere: '',
  taux_devise: 0,
  items: [],
  remise_pourcentage: 0,
  remise_montant: 0,
  taxes_additionnelles: [],
  facture_importee_id: '',
  facture_origine_id: '',
  notes: '',
};

// ============================================================================
// Store
// ============================================================================

export const useInvoiceStore = create<InvoiceState>()((set, get) => ({
  // State initial
  fneInvoices: [],
  fneInvoicesTotal: 0,
  fneInvoicesLoading: false,
  fneInvoicesFilters: { page: 1, limit: 20 },
  importedInvoices: [],
  importedInvoicesTotal: 0,
  importedInvoicesLoading: false,
  currentInvoice: null,
  currentInvoiceItems: [],
  currentInvoiceLoading: false,
  formStep: 1,
  formData: { ...initialFormData },
  formErrors: {},
  formSubmitting: false,
  editingInvoiceId: null,
  importProgress: null,
  importRunning: false,
  error: null,

  // --- Actions liste ---
  setFneInvoices: (fneInvoices, fneInvoicesTotal) =>
    set({ fneInvoices, fneInvoicesTotal, fneInvoicesLoading: false }),

  setFneInvoicesLoading: (fneInvoicesLoading) => set({ fneInvoicesLoading }),

  setFneInvoicesFilters: (filters) =>
    set((state) => ({
      fneInvoicesFilters: { ...state.fneInvoicesFilters, ...filters },
    })),

  setImportedInvoices: (importedInvoices, importedInvoicesTotal) =>
    set({ importedInvoices, importedInvoicesTotal, importedInvoicesLoading: false }),

  setImportedInvoicesLoading: (importedInvoicesLoading) => set({ importedInvoicesLoading }),

  // --- Actions facture courante ---
  setCurrentInvoice: (currentInvoice, currentInvoiceItems = []) =>
    set({ currentInvoice, currentInvoiceItems, currentInvoiceLoading: false }),

  setCurrentInvoiceLoading: (currentInvoiceLoading) => set({ currentInvoiceLoading }),

  // --- Actions formulaire ---
  setFormStep: (formStep) => set({ formStep, formErrors: {} }),

  nextStep: () => set((state) => ({ formStep: Math.min(state.formStep + 1, 7), formErrors: {} })),

  prevStep: () => set((state) => ({ formStep: Math.max(state.formStep - 1, 1), formErrors: {} })),

  updateFormData: (updates) =>
    set((state) => ({
      formData: { ...state.formData, ...updates },
    })),

  addItem: (item) =>
    set((state) => ({
      formData: { ...state.formData, items: [...state.formData.items, item] },
    })),

  updateItem: (tempId, updates) =>
    set((state) => ({
      formData: {
        ...state.formData,
        items: state.formData.items.map((it) =>
          it._temp_id === tempId ? { ...it, ...updates } : it
        ),
      },
    })),

  removeItem: (tempId) =>
    set((state) => ({
      formData: {
        ...state.formData,
        items: state.formData.items.filter((it) => it._temp_id !== tempId),
      },
    })),

  addTaxeAdditionnelle: (tax) =>
    set((state) => ({
      formData: {
        ...state.formData,
        taxes_additionnelles: [...state.formData.taxes_additionnelles, tax],
      },
    })),

  removeTaxeAdditionnelle: (index) =>
    set((state) => ({
      formData: {
        ...state.formData,
        taxes_additionnelles: state.formData.taxes_additionnelles.filter((_, i) => i !== index),
      },
    })),

  setFormErrors: (formErrors) => set({ formErrors }),
  clearFormErrors: () => set({ formErrors: {} }),
  setFormSubmitting: (formSubmitting) => set({ formSubmitting }),

  prefillFromImport: (imported) =>
    set({
      formStep: 1,
      editingInvoiceId: null,
      formData: {
        ...initialFormData,
        client_nom: imported.nom_client || '',
        client_ncc: imported.ncc_client || '',
        facture_importee_id: imported.id,
        // Les données transformées seront utilisées pour pré-remplir davantage
        ...(imported.donnees_transformees as Partial<InvoiceFormData> || {}),
      },
      formErrors: {},
    }),

  prefillFromDraft: (invoice, items) => {
    const taxesAdd = (() => {
      try {
        const raw = invoice.taxes_additionnelles;
        if (Array.isArray(raw)) return raw.map((t: any) => ({ nom: t.name || t.nom || '', montant: Number(t.amount || t.montant || 0) }));
        if (typeof raw === 'string') {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed.map((t: any) => ({ nom: t.name || t.nom || '', montant: Number(t.amount || t.montant || 0) }));
        }
        return [];
      } catch { return []; }
    })();

    set({
      formStep: 1,
      editingInvoiceId: invoice.id,
      formData: {
        etablissement: invoice.etablissement || '',
        point_de_vente: invoice.point_de_vente || '',
        message_commercial: invoice.message_commercial || '',
        pied_de_page: invoice.pied_de_page || '',
        type_facture: (invoice.type_facture as any) || '',
        mode_paiement: (invoice.mode_paiement as any) || '',
        template: (invoice.template as any) || '',
        est_rne: invoice.est_rne || false,
        numero_rne: invoice.numero_rne || '',
        client_ncc: invoice.client_ncc || '',
        client_nom: invoice.client_nom || '',
        client_telephone: invoice.client_telephone || '',
        client_email: invoice.client_email || '',
        client_vendeur: invoice.client_vendeur || '',
        devise_etrangere: invoice.devise_etrangere || '',
        taux_devise: Number(invoice.taux_devise) || 0,
        items: items.map((item) => ({
          _temp_id: `edit_${item.id}`,
          quantite: Number(item.quantite) || 1,
          reference: item.reference || '',
          designation: item.designation || '',
          prix_unitaire_ht: Number(item.prix_unitaire_ht) || 0,
          remise_pourcentage: Number(item.remise_pourcentage) || 0,
          taux_tva: item.taux_tva != null ? Number(item.taux_tva) : 18,
          type_tva: (item.type_tva as VatType) || 'TVA',
          unite_mesure: item.unite_mesure || UNITE_DEFAULT,
          taxes_personnalisees: (() => {
            try {
              const raw = item.taxes_personnalisees;
              if (Array.isArray(raw)) return raw.map((t: any) => ({ nom: t.name || t.nom || '', montant: Number(t.amount || t.montant || 0) }));
              if (typeof raw === 'string') {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed.map((t: any) => ({ nom: t.name || t.nom || '', montant: Number(t.amount || t.montant || 0) }));
              }
              return [];
            } catch { return []; }
          })(),
          article_id: item.article_id || '',
        })),
        remise_pourcentage: Number(invoice.remise_pourcentage) || 0,
        remise_montant: Number(invoice.remise_montant) || 0,
        taxes_additionnelles: taxesAdd,
        facture_importee_id: invoice.facture_importee_id || '',
        facture_origine_id: invoice.facture_origine_id || '',
        notes: invoice.notes || '',
      },
      formErrors: {},
    });
  },

  resetForm: () => set({ formStep: 1, formData: { ...initialFormData }, formErrors: {}, formSubmitting: false, editingInvoiceId: null }),

  // --- Actions import ---
  setImportProgress: (importProgress) => set({ importProgress }),
  setImportRunning: (importRunning) => set({ importRunning }),

  // --- Erreur ---
  setError: (error) => set({ error }),
}));
